/**
 * Formatação de número, dinheiro e data — um lugar só.
 *
 * A auditoria de 11/ago/2026 achou `brl` definido DEZ vezes, com
 * quatro contratos diferentes para o mesmo caso: `Admin.tsx` devolve
 * '—' para null, `Agendar.tsx` devolve null, `Caixa.tsx` nem aceita
 * null e `MeuPainel.tsx` trata null como zero. Numa tela de dinheiro
 * isso não é cosmético: "R$ 0,00" e "—" contam histórias diferentes
 * (não entrou nada × não sabemos).
 *
 * Contrato daqui: **null e undefined viram travessão; zero vira
 * R$ 0,00.** Se o dado é ausente, a tela diz que é ausente.
 *
 * Toda API do Clinuma manda dinheiro em CENTAVOS, e como `bigint` do
 * Postgres chega como string. Por isso a entrada é `number | string`.
 */

export const TRAVESSAO = '—'

const numero = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const fmtBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
})
const fmtBRLCompacto = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
  notation: 'compact', maximumFractionDigits: 1,
})
const fmtInt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })

/** Centavos → "R$ 1.234,56". Null → "—". */
export function brl(centavos: number | string | null | undefined): string {
  const n = numero(centavos)
  return n === null ? TRAVESSAO : fmtBRL.format(n / 100)
}

/**
 * Centavos → "R$ 1,2 mil". Só para EIXO e para o valor grande do
 * cartão de KPI, onde a casa dos centavos é ruído. Nunca numa tabela
 * de conferência: quem confere caixa precisa do centavo.
 */
export function brlCompacto(centavos: number | string | null | undefined): string {
  const n = numero(centavos)
  if (n === null) return TRAVESSAO
  return Math.abs(n) < 100_000 ? fmtBRL.format(n / 100) : fmtBRLCompacto.format(n / 100)
}

/** 1234 → "1.234". Null → "—". */
export function inteiro(v: number | string | null | undefined): string {
  const n = numero(v)
  return n === null ? TRAVESSAO : fmtInt.format(n)
}

/**
 * 0,824 → "82%". `casas` só quando o número for pequeno e a diferença
 * importar (taxa de falta de 2,4% × 2,9%).
 */
export function pct(v: number | string | null | undefined, casas = 0): string {
  const n = numero(v)
  if (n === null) return TRAVESSAO
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  })}%`
}

/** Delta com sinal explícito: "+5,2%" / "−3,0%" / "0%". */
export function delta(v: number | null | undefined, casas = 1): string {
  const n = numero(v)
  if (n === null) return TRAVESSAO
  if (n === 0) return '0%'
  const s = Math.abs(n).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  })
  return `${n > 0 ? '+' : '−'}${s}%`
}

/** Minutos → "1h20" / "45min". Ocupação de agenda vive nisto. */
export function duracao(min: number | string | null | undefined): string {
  const n = numero(min)
  if (n === null) return TRAVESSAO
  const h = Math.floor(n / 60), m = Math.round(n % 60)
  if (h === 0) return `${m}min`
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`
}

/* ── datas ─────────────────────────────────────────────────────────
   `data` recebe ISO (`2026-08-11`) ou Date. Tudo em pt-BR, tudo no
   fuso do navegador — que é o da clínica.                          */

const comoData = (v: string | number | Date): Date =>
  v instanceof Date ? v
    : typeof v === 'number' ? new Date(v)
    // "2026-08-11" sem hora é interpretado como UTC pelo JS e volta um
    // dia atrás em qualquer fuso negativo. O T00:00 força local.
    : /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`)
    : new Date(v)

export const data = (v: string | number | Date | null | undefined): string =>
  v == null ? TRAVESSAO : comoData(v).toLocaleDateString('pt-BR')

export const dataCurta = (v: string | number | Date | null | undefined): string =>
  v == null ? TRAVESSAO
    : comoData(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })

export const hora = (v: string | number | Date | null | undefined): string =>
  v == null ? TRAVESSAO
    : comoData(v).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

export const dataHora = (v: string | number | Date | null | undefined): string =>
  v == null ? TRAVESSAO : `${data(v)} ${hora(v)}`

/** "2026-08" ou "2026-08-01" → "ago/26". Rótulo de eixo de mês. */
export function mesCurto(competencia: string | null | undefined): string {
  if (!competencia) return TRAVESSAO
  const [a, m] = competencia.split('-')
  if (!a || !m) return TRAVESSAO
  const d = new Date(Number(a), Number(m) - 1, 1)
  return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
    .replace('.', '')
}

export const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const
